import { motion } from "framer-motion";

interface LabLoaderProps {
  size?: "sm" | "md" | "lg";
  text?: string;
  fullScreen?: boolean;
}

export function LabLoader({ size = "md", text, fullScreen = false }: LabLoaderProps) {
  const sizes = {
    sm: { tube: "w-8 h-16", bubbles: 3 },
    md: { tube: "w-12 h-24", bubbles: 5 },
    lg: { tube: "w-16 h-32", bubbles: 7 },
  };

  const { tube, bubbles } = sizes[size];

  const loaderContent = (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="relative">
        <svg
          viewBox="0 0 60 120"
          className={tube}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <clipPath id="tubeClip">
              <path d="M15 30 L15 100 Q15 110 25 110 L35 110 Q45 110 45 100 L45 30 Z" />
            </clipPath>
            <linearGradient id="liquidGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.9" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
            </linearGradient>
          </defs>
          
          <path
            d="M20 10 L20 25 L15 30 L15 100 Q15 110 25 110 L35 110 Q45 110 45 100 L45 30 L40 25 L40 10 Z"
            stroke="hsl(var(--foreground))"
            strokeWidth="2"
            fill="none"
            className="opacity-30"
          />
          
          <path
            d="M22 10 L38 10"
            stroke="hsl(var(--foreground))"
            strokeWidth="3"
            strokeLinecap="round"
            className="opacity-40"
          />

          <g clipPath="url(#tubeClip)">
            <motion.rect
              x="15"
              y="110"
              width="30"
              height="80"
              fill="url(#liquidGradient)"
              initial={{ y: 110 }}
              animate={{ y: [110, 30, 110] }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            
            {[...Array(bubbles)].map((_, i) => (
              <motion.circle
                key={i}
                cx={20 + Math.random() * 20}
                r={2 + Math.random() * 2}
                fill="hsl(var(--primary-foreground))"
                opacity={0.6}
                initial={{ cy: 110, opacity: 0 }}
                animate={{
                  cy: [110, 40 + Math.random() * 30],
                  opacity: [0, 0.7, 0],
                }}
                transition={{
                  duration: 1.5 + Math.random(),
                  repeat: Infinity,
                  delay: i * 0.3,
                  ease: "easeOut",
                }}
              />
            ))}
          </g>
          
          <g className="opacity-20">
            {[35, 50, 65, 80, 95].map((y, i) => (
              <line
                key={i}
                x1="46"
                y1={y}
                x2="50"
                y2={y}
                stroke="hsl(var(--foreground))"
                strokeWidth="1"
              />
            ))}
          </g>
        </svg>
      </div>
      
      {text && (
        <motion.p
          className="text-sm text-muted-foreground font-medium"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
}

export function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <LabLoader size="lg" text="Preparando..." />
    </div>
  );
}

export function InlineLoader({ text }: { text?: string }) {
  return (
    <div className="flex items-center justify-center py-8">
      <LabLoader size="sm" text={text} />
    </div>
  );
}
